import React from "react";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { FlexIgnoreZonesCameraPresetCard } from "./FlexIgnoreZonesCameraPresetCard";
import { CameraContext, FlexCameraSummary, FlexPreset } from "./types";
import {
  FLEX_IZ_CREATE_NEW_PRESET_OPTION,
  FLEX_IZ_DEFAULT_CAMERA_CONFIG_LABEL,
  FLEX_IZ_DEFAULT_CAMERA_CONFIG_OPTION,
  FLEX_IZ_NO_IGNORE_ZONES_OPTION,
} from "./flexIzPresetSelectConstants";

export const FlexIgnoreZonesEditPresetCard = ({
  scheduleId,
  camera,
  preset,
  cameraContext,
  selectablePresets,
  selectedPresetLocalId,
  onSelectPreset,
  onCreateNewPreset,
  onPreviewImageError,
  showNoIgnoreZonesOption = true,
  showDefaultCameraConfigOption = false,
}: {
  scheduleId: number;
  camera: FlexCameraSummary;
  preset: FlexPreset;
  cameraContext?: CameraContext;
  selectablePresets: FlexPreset[];
  selectedPresetLocalId: string;
  onSelectPreset: (presetLocalId: string) => void;
  onCreateNewPreset: () => void;
  onPreviewImageError?: () => void;
  showNoIgnoreZonesOption?: boolean;
  /**
   * Offer "Default" (camera-level zones/lines, persisted as `preset: null`).
   * Post-onboarding schedule editing only; the onboarding wizard creates presets.
   */
  showDefaultCameraConfigOption?: boolean;
}) => {
  const labelId = `flex-iz-edit-preset-${scheduleId}-${camera.id}`;
  const hasNoIgnoreZonesOption = selectablePresets.some(
    (preset) =>
      preset.localId === FLEX_IZ_NO_IGNORE_ZONES_OPTION ||
      (preset.name || "").trim().toLowerCase() === "no ignore zones"
  );

  const presetDropdown = (
    <FormControl fullWidth size="small" sx={{ fontFamily: "Mulish" }}>
      <InputLabel id={labelId} sx={{ fontFamily: "Mulish" }}>
        Ignore Zones Preset
      </InputLabel>
      <Select
        labelId={labelId}
        label="Ignore Zones Preset"
        value={selectedPresetLocalId}
        onChange={(event) => {
          const nextValue = event.target.value as string;
          if (nextValue === FLEX_IZ_CREATE_NEW_PRESET_OPTION) {
            onCreateNewPreset();
            return;
          }
          onSelectPreset(nextValue);
        }}
        MenuProps={{
          PaperProps: {
            sx: { bgcolor: "#fff", fontFamily: "Mulish" },
          },
        }}
        sx={{
          fontFamily: "Mulish",
          bgcolor: "#fff",
          borderRadius: "6px",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(0, 0, 0, 0.18)",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(0, 0, 0, 0.35)",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#001943",
            borderWidth: "1px",
          },
          "& .MuiSelect-select": {
            py: 1.25,
          },
        }}
      >
        {showDefaultCameraConfigOption && (
          <MenuItem value={FLEX_IZ_DEFAULT_CAMERA_CONFIG_OPTION}>
            {FLEX_IZ_DEFAULT_CAMERA_CONFIG_LABEL}
          </MenuItem>
        )}
        {selectablePresets.map((p) => (
          <MenuItem value={p.localId} key={p.localId}>
            {p.name}
          </MenuItem>
        ))}
        <MenuItem
          value={FLEX_IZ_CREATE_NEW_PRESET_OPTION}
          sx={{
            color: "#FF8400",
            fontFamily: "Mulish",
            fontWeight: 700,
          }}
        >
          + CREATE NEW PRESET
        </MenuItem>
        {showNoIgnoreZonesOption && !hasNoIgnoreZonesOption && (
          <MenuItem value={FLEX_IZ_NO_IGNORE_ZONES_OPTION}>
            No Ignore Zones
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );

  return (
    <FlexIgnoreZonesCameraPresetCard
      scheduleId={scheduleId}
      camera={camera}
      preset={preset}
      cameraContext={cameraContext}
      presetLineReplacement={presetDropdown}
      onPreviewImageError={onPreviewImageError}
    />
  );
};
